let _ = require('../_.js');

//
// websocket.js
//
// Websocket client / interface.  Connects on demand to the backing websocket server and delegates sending 
// and receiving messages with the server.
//

let connecting = false;

// Configuration:

// Max number of batched messages to send to WS in a single request
_.ws_debug_verbose = false;          // Set to true to see a whole bunch of stuff about all WS operations on the console

// Toggle verbose client-side WS logging. Call _.rt_debug() from the browser console.
// Server-side debug logging is the WS server's HTTP /debug endpoint (server secret).
_.rt_debug = function (enable = true) {
    _.ws_debug_verbose = enable;
};

let ping_interval = 25000;           // While connected, send keep alive / check latency every X ms.
let dead_socket_after = 60000;       // No message (pong or other) for this long: the socket is dead (half-open after sleep / NAT drop)
let stable_connection_after = 10000; // A connection this old resets the reconnect backoff (a server closing us at once keeps backing off)

// Set true by pages whose latency the clerk shows (a logged-in bidder on the website). Everyone
// else only pings to keep the connection alive.
_.websocket_report_latency = false;
let disconnect_grace_period = 5000;  // Amount of time between a disconnect while we attempt to reconnect before we actually show a disconnect message

// Public interfaces:

// Connects to websocket if the connection has not yet been established.
_.ws_connect = function () {
    _connect();
}

_.ws_reconnect = _.debounce(5000, async function () {
    console.log("Reconnecting to WS");
    _disconnect();
    _reconnect();
});

// Sends a message on websocket.  Messages sent through here are debounced and sent when all of 
// the main thread processing is done.  If websocket is not connected, it connects to it it.
_.websocket_send = function (topic, data) {
    send_queue.push({ topic: topic, data: data });
    _connect();
    _process_send_queue();
}

// Like websocket_send, but re-sends all the messages on WS disconnect.  So, send messages
// related to subscriptions through here and we can be sure we will get a response 
// when the websocket server comes back online.
_.websocket_send_subscribe = function (topic, data) {
    // Same object in both lists: onopen dedupes the queue against subscription_messages
    let message = { topic: topic, data: data };
    subscription_messages.push(message);
    send_queue.push(message);
    _connect();
    _process_send_queue();
}

// Subscribe to receiving messages from the websocket server.
_.on_websocket_receive = function (callback) {
    receive_callbacks.push(callback);
}

// callback called once when we are attempting to connect to websocket.
_.on_websocket_connecting = function (callback) {
    connecting_callbacks.push(callback);
}

// callback called once a connection to websocket has been established, either the
// first time, or after a disconnect
_.on_websocket_connected = function (callback) {
    connected_callbacks.push(callback);
}

// callback called once a connection to websocket has been re-established, after a 
// on_websocket_disconnected_show_error event happened.
_.on_websocket_connected_show_reconnect = function (callback) {
    connected_callbacks_show_reconnect.push(callback);
}

// callback called when websocket connection lost, all data should be marked dirty
// and an error should display
_.on_websocket_disconnected = function (callback) {
    disconnected_callbacks.push(callback);
}

// callback called after 3 seconds of disconnect, this is given in case websocket gets immediatly reconnected.
// and an error should display
_.on_websocket_disconnected_show_error = function (callback) {
    disconnected_callbacks_show_error.push(callback);
}

// callback called when this connection reached a different WS server process than the previous
// one (server_epoch changed): the server lost the revisions it would have caught us up with, and
// updates posted while it was down never arrived. Records held must be refetched (core/data.js).
_.on_websocket_server_restarted = function (callback) {
    server_restarted_callbacks.push(callback);
}

// Returns the current latency of the connection in ms.
_.get_websocket_latency = function () {
    return ws_latency;
}



// ----- Private -----

let connected = false;

let connecting_callbacks = [];
let connected_callbacks = [];
let connected_callbacks_show_reconnect = [];
let disconnected_callbacks = [];
let disconnected_callbacks_show_error = [];
let receive_callbacks = [];
let server_restarted_callbacks = [];
let subscription_messages = [];
let send_queue = [];
let socket = null;
let reported_console_error = false;
let reported_show_error = false;
let ws_ping_uid = 0;
let ws_ping_time = 0;
let ws_last_ping_response_time = null;  // Initialize to null to indicate no response received yet
let server_epoch = null;             // Epoch of the WS server process we last connected to

let ws_latency = null;
let reconnect_attempt = 0;          // Failed attempts since the last connection; drives the backoff
let reconnect_timer = null;         // The one pending reconnect (never more than one)
let stable_timer = null;            // Resets reconnect_attempt once a connection has lasted stable_connection_after

// Reconnect with exponential backoff and jitter: first try after 0.5-2 s, then up to 30 s apart.
// The jitter spreads thousands of viewers out after a WS server restart instead of all
// reconnecting (and re-subscribing) in the same second.
let _schedule_reconnect = function () {
    if (reconnect_timer !== null) return;
    let delay = reconnect_attempt == 0
        ? 500 + Math.random() * 1500
        : Math.min(30000, 1000 * Math.pow(2, reconnect_attempt)) * (0.5 + Math.random() * 0.5);
    reconnect_attempt++;
    reconnect_timer = setTimeout(function () {
        reconnect_timer = null;
        _reconnect();
    }, delay);
};


// If connection has never been attempted, attempt to connect.  The connection will stay open for
// the remainder of the browser session.
let _connect = function () {
    setTimeout(function () {
        // This happens once.  Past here all reconnects happen automatically.
        if (connecting) return;
        connecting = true;

        for (let i in connecting_callbacks) {
            connecting_callbacks[i]();
        }

        _reconnect();
    }, 250);
}

let _disconnect = function () {
    if (!connected) return;

    try {
        socket.close();
    } catch (e) {
    }
}

// Makes a new websocket connection, called on first connect or after disconnect.  It is assumed that this never gets
// called outside of this private code.
let _reconnect = function () {

    if (connected) return;

    if (socket !== null) return;

    try {
        socket = new WebSocket(_.websocket_endpoint);
    } catch (e) {
        // Silence websocket connection error messages
        console.error(e);
        handleDisconnect();
        return;
    }

    if (!socket) {
        console.log("error opening ws at " + _.websocket_endpoint);
        _.shouldnt_happen();
        return;
    }

    socket.onopen = function (e) {
        console.log("WS: Connected");

        connected = true;
        ws_last_ping_response_time = new Date().getTime();
        ws_latency = null;
        reported_console_error = false;
        clearTimeout(stable_timer);
        stable_timer = setTimeout(function () {
            reconnect_attempt = 0;
        }, stable_connection_after);

        for (let i in connected_callbacks) {
            let result = connected_callbacks[i]();
            if (result instanceof Promise) {
                console.error('Error: Callback returned a Promise. on_websocket_connected() callbacks must be synchronous.');
            }
        }

        // Was a previous disconnected error shown?  If so, cause the associated connected message to be shown.
        if (reported_show_error) {
            reported_show_error = false;
            for (let i in connected_callbacks_show_reconnect) {
                let result = connected_callbacks_show_reconnect[i]();
                if (result instanceof Promise) {
                    console.error('Error: Callback returned a Promise. on_websocket_connected_show_reconnect() callbacks must be synchronous.');
                }
            }
        }

        // Get latency
        _ping();

        // Every subscription is (re)sent once per connection, ahead of anything else queued. The
        // replayed model_subscribe messages carry the last revision we know, so the server
        // answers with catch-up updates.
        send_queue = subscription_messages.concat(send_queue.filter(m => !subscription_messages.includes(m)));

        // Process any queued up messages
        _process_send_queue();
    };

    socket.onmessage = function (e) {
        // Any message proves the socket alive (dead socket check in _ping)
        ws_last_ping_response_time = new Date().getTime();

        // console.log("Received message ", e);
        let parsed = JSON.parse(e.data);

        // Wrapped in a _core_realtime, see async.js for explanation
        if (!Array.isArray(parsed)) {
            console.error("WS: response from server should be an array", e.data);
            return;
        }

        for (let i in parsed) {
            let record = parsed[i];

            if (!record.topic || !record.data) {
                console.error("WS: each message should have topic and data", record);
                continue;
            }

            let topic = record.topic;
            let data = record.data;

            if (_.ws_debug_verbose) {
                console.log("WS: Receive [" + topic + "] ", data)
            }

            if (topic == "console_error") {
                console.error("WS (error): ", data.message, { request: data.request });
                continue;
            }

            if (topic == "console_log") {
                console.log("WS (log): ", data.request[0]);
                continue;
            }

            // Add 'latency' attribute to pong responses.
            if (topic == "pong") {
                ws_last_ping_response_time = new Date().getTime();

                if (data.uid == ws_ping_uid) {
                    ws_latency = new Date().getTime() - ws_ping_time;
                    data.latency = ws_latency * 1;
                    if (_.websocket_report_latency) {
                        _.websocket_send("client_latency", { 'latency': ws_latency });
                    }
                } else {
                    continue;
                }
            }

            // Server forced reload of client
            if (topic == "root_refresh_reload_all") {
                window.location.reload();
            }

            // Sent by the server on connect. A new epoch on a reconnect = the server restarted.
            if (topic == "server_epoch") {
                if (server_epoch !== null && server_epoch !== data.epoch) {
                    for (let callback of server_restarted_callbacks) {
                        callback();
                    }
                }
                server_epoch = data.epoch;
                continue;
            }

            for (let i in receive_callbacks) {
                let result = receive_callbacks[i](topic, data);
                if (result instanceof Promise) {
                    console.error('Error: Callback returned a Promise. on_websocket_disconnected() callbacks must be synchronous.');
                }
            }
        }
    };

    // This often happens on connection failure.  we ignore these. close always happens next.
    socket.onerror = function (event) {
        if (_.ws_debug_verbose) console.error("WS: error observed:", event);
    };

    socket.onclose = function () {
        handleDisconnect();
    };
}

let handleDisconnect = function () {
    if (!reported_console_error) {
        reported_console_error = true;
        console.error('WS: Disconnected.  Reconnecting with backoff.');
    }

    connected = false;
    ws_latency = null;
    socket = null;
    clearTimeout(stable_timer);

    // Make all callbacks for connection disconnect.
    for (let i in disconnected_callbacks) {
        let result = disconnected_callbacks[i]();
        if (result instanceof Promise) {
            console.error('Error: Callback returned a Promise. on_websocket_disconnected() callbacks must be synchronous.');
        }
    }

    _schedule_reconnect();

    // Wait briefly before actually calling the callbacks which visually show the disconnect error to the user.
    setTimeout(function () {
        if (!connected && !reported_show_error) {
            reported_show_error = true;
            for (let i in disconnected_callbacks_show_error) {
                let result = disconnected_callbacks_show_error[i]();
                if (result instanceof Promise) {
                    console.error('Error: Callback returned a Promise. on_websocket_disconnected_show_error() callbacks must be synchronous.');
                }
            }
        }
    }, disconnect_grace_period);
}

// Ping / Keepalive every 15 seconds

setInterval(function () {
    _ping();
}, ping_interval);

let _ping = function () {
    // Initialize last ping response time to now if null
    if (ws_last_ping_response_time === null) {
        ws_last_ping_response_time = new Date().getTime();
    }

    if (connected) {
        // Nothing heard for dead_socket_after: the socket is dead though the browser still calls it open
        // (sleep, network change, NAT timeout). Drop it and reconnect, so the page shows the disconnect and
        // resynchronizes instead of silently showing stale data.
        if (new Date().getTime() - ws_last_ping_response_time > dead_socket_after) {
            console.error('WS: no response for ' + (dead_socket_after / 1000) + 's, reconnecting');
            let dead = socket;
            dead.onclose = null;
            dead.onmessage = null;
            try {
                dead.close();
            } catch (e) {
            }
            handleDisconnect();
            return;
        }

        // Send a new ping
        ws_ping_uid++;
        ws_ping_time = new Date().getTime();
        _.websocket_send("ping", { 'uid': ws_ping_uid });
    }
};

// Call this to try to send a batch of messages to the server
let _process_send_queue = _.debounce(200, async function () {
    if (!connected || send_queue.length == 0) {
        return;
    }

    let this_batch = send_queue.splice(0, Math.min(_.websocket_max_per_batch, send_queue.length));

    let this_message = JSON.stringify(this_batch);

    if (_.ws_debug_verbose) for (let i in this_batch) {
        console.log("WS: Sending message to server: [" + this_batch[i].topic + "] ", this_batch[i].data)
    }

    try {
        socket.send(this_message);
    } catch (e) {
        console.error("There was a problem sending ws payload, retrying in 5 seconds", this_batch);

        await _.sleep(5 * 1000);

        // Batch the messages up again
        for (let i in this_batch) {
            _.websocket_send(this_batch[i].topic, this_batch[i].data);
        }

        return;
    }

    // Send the next batch
    if (send_queue.length > 0) {
        _process_send_queue();
    }
});

